import type { FC } from 'react';
import { memo, useCallback } from 'react';

import type { TModalProps } from '~components/Modals/types';
import { annals } from '~features/annals/hooks';
import type { ReportMessageParams } from '~features/report/model/types';
import { ReportModal } from '~features/report/ui/ReportModal';
import { useTranslations } from '~i18n/hooks';
import { dialogsApi } from '~services/api/dialogs';
import {
	DatadogLogger,
	LoggerMessages,
	LoggerServices,
} from '~shared/services/logger';

export const ReportMessageModal: FC<TModalProps & ReportMessageParams> = memo(
	({ open, onClose, locked, botName, message, source, onReported }) => {
		const t = useTranslations('Report.reportMessage');
		const reportMessageCategories = (
			(t.raw('reportMessageCategories') || []) as { value: string }[]
		).map((it) => it.value);
		const [deleteMessageAction] = dialogsApi.useDeleteMessageMutation();

		const trackReportMessageSubmitted = annals.report.useReportSubmitted();

		const action = useCallback(
			(selectedCategory?: string) => {
				if (!!message && !!source) {
					onReported?.();
					void trackReportMessageSubmitted({
						MsgId: message.id,
						source,
						reason: selectedCategory,
					});
					void deleteMessageAction({
						senderId: message.sender,
						recipientId: message.recipient,
						messageId: message.id,
					});
				} else {
					DatadogLogger.warn(
						LoggerServices.ChatService,
						LoggerMessages.CannotDeleteMessage,
						{
							message,
							source,
						}
					);
				}
			},
			[
				deleteMessageAction,
				message,
				source,
				trackReportMessageSubmitted,
				onReported,
			]
		);

		return (
			<ReportModal
				title={t('reportMessageModalTitle')}
				subtitle={t('reportMessageModalSubtitle', { name: botName || '' })}
				buttonText={t('reportMessageModalButton')}
				categories={reportMessageCategories}
				open={open}
				locked={locked}
				action={action}
				onClose={onClose}
			/>
		);
	}
);

ReportMessageModal.displayName = 'ReportMessageModal';
